import { useState } from 'react';
import { ChevronDown, ChevronRight, Flame, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import type { TopQueryStats } from '@easyflow/shared';

interface Props {
  queries: TopQueryStats[];
  available: boolean;
}

function fmtMs(v: number): string {
  if (v < 1) return `${(v * 1000).toFixed(0)} µs`;
  if (v < 1000) return `${v.toFixed(1)} ms`;
  return `${(v / 1000).toFixed(2)} s`;
}

export function TopQueriesCard({ queries, available }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) => {
    const n = new Set(expanded);
    if (n.has(id)) n.delete(id); else n.add(id);
    setExpanded(n);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Flame className="h-5 w-5 text-amber-500" />
          Top query (pg_stat_statements)
          <Badge variant="outline" className="ml-1 text-xs">{queries.length}</Badge>
        </CardTitle>
        <CardDescription>Top 20 by total execution time</CardDescription>
      </CardHeader>
      <CardContent>
        {!available ? (
          <div className="p-4 rounded border border-amber-200 bg-amber-50 dark:bg-amber-950/30 text-sm text-amber-800 dark:text-amber-200 flex items-start gap-2">
            <Info className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold mb-1">pg_stat_statements non disponibile</p>
              <p className="text-xs">
                Verifica che <code className="font-mono">shared_preload_libraries='pg_stat_statements'</code> sia
                attivo nel drop-in tuning Patroni e che la migration <code className="font-mono">CREATE EXTENSION
                pg_stat_statements</code> sia stata applicata. Restart Postgres richiesto se aggiunto al
                preload-libraries.
              </p>
            </div>
          </div>
        ) : queries.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">Nessuna query tracciata</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                <TableHead>Query</TableHead>
                <TableHead className="text-right">Calls</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Mean</TableHead>
                <TableHead className="text-right">Rows</TableHead>
                <TableHead className="text-right">Cache hit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queries.map((q) => {
                const open = expanded.has(q.queryid);
                const hitPct = q.shared_blks_hit_ratio === null ? null : (q.shared_blks_hit_ratio * 100);
                return (
                  <TableRow key={q.queryid} className="align-top">
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => toggle(q.queryid)}
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={open ? 'Comprimi' : 'Espandi'}
                      >
                        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </button>
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xl">
                      {open ? (
                        <pre className="whitespace-pre-wrap break-words text-xs">{q.query}</pre>
                      ) : (
                        <div className="truncate" title={q.query}>{q.query}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{q.calls.toLocaleString('it-IT')}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtMs(q.total_exec_ms)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtMs(q.mean_exec_ms)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{q.rows.toLocaleString('it-IT')}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {hitPct === null ? '—' : (
                        <span className={hitPct >= 95 ? 'text-emerald-600' : hitPct >= 80 ? 'text-amber-600' : 'text-rose-600'}>
                          {hitPct.toFixed(1)}%
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
